import { Box, Button, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useFormik } from 'formik';
import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddNewIcon, ExportFillIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { cast, clearInputFilter, exportExcel, handleApiError, notify } from '~/utils/helper';
import styles from './Technician.module.scss';
import { IAccount, IGetAllParams } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import dayjs from 'dayjs';
import { ROLE_TECHINICIAN } from '~/utils/constants/account';
import TechnicianTable from './component/TechnicianTable';
import DetailTechnicianDialog from './component/DetailTechnicianDialog';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import EditTechnicianDialog from './component/EditTechnicianDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import UpdatePasswordDialog from './component/UpdatePasswordDialog';

const cx = classNames.bind(styles);

const initialValues: IGetAllParams = {
  date_to: '',
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const Technician = () => {
  const { t } = useTranslation('admin/technician');
  const [data, setAccount] = useState<IAccount[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshData, setRefreshData] = useState(false);
  const [isOpenDetailTechnicianDialog, setIsOpenDetailTechnicianDialog] = useState<boolean>(false);
  const [isOpenDeleteTechnicianDialog, setIsOpenDeleteTechnicianDialog] = useState<boolean>(false);
  const [isOpenEditTechnicianDialog, setIsOpenEditTechnicianDialog] = useState<boolean>(false);
  const technician = useRef<IAccount | null>(null);

  const [openUpdatePasswordDialog, setOpenUpdatePasswordDialog] = useState<boolean>(false);
  const [technicianIds, setTechnicianIds] = useState<Array<string>>([]);

  const ratingStatusOptions = [
    {
      id: 1,
      name: t('ratingStatus.improvement'),
    },
    {
      id: 2,
      name: t('ratingStatus.meetRequirement'),
    },
    {
      id: 3,
      name: t('ratingStatus.excellence'),
    },
  ];

  useEffect(() => {
    if (refreshData) {
      handleGetTechnicians();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  const form = useFormik<IGetAllParams>({
    initialValues,
    onSubmit: async () => {
      handleGetTechnicians();
    },
  });

  useEffect(() => {
    handleGetTechnicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size]);

  useEffect(() => {
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  const getTechnicians = async (overrideParams = {}) => {
    try {
      return (
        await accountApi.getByRoleList(ROLE_TECHINICIAN, {
          ...form.values,
          ...overrideParams,
        })
      ).data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetTechnicians = async () => {
    const response = await getTechnicians();
    setTotalItems(response.total);
    setAccount(response.data);
  };

  const handleDeleteTechnician = async (needRefresh?: boolean) => {
    try {
      if (technician.current) {
        await accountApi.deleteByRole({ username: technician.current.username, role: ROLE_TECHINICIAN });
        if (needRefresh) {
          handleGetTechnicians();
        }
        notify('success', t('deleteTechnicianDialog.deleteSuccess'));
        setIsOpenDeleteTechnicianDialog(false);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCheckAll = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const res = await getTechnicians({ page_index: 1, page_size: totalItems });
      setTechnicianIds(res.data?.map((account: IAccount) => cast(account.username)));
    } else {
      setTechnicianIds([]);
    }
  };

  const handleCheck = async (row: IAccount, checked: boolean) => {
    if (checked) {
      setTechnicianIds((prev) => [...(prev ?? []), ...[cast(row.username)]]);
    } else {
      setTechnicianIds(technicianIds.filter((account) => account !== row.username));
    }
  };

  const exportTechnicians = async () => {
    if (!technicianIds.length) return notify('error', t('emptyExportFile'));
    const response = await getTechnicians({
      page_index: 1,
      page_size: totalItems,
    });

    let res = response.data;
    if (technicianIds.length) {
      const filterSet = new Set(technicianIds);
      res = response.data?.filter((item) => filterSet.has(cast(item.username)));
      if (!res.length) {
        setTechnicianIds([]);
        res = response.data;
      }
    }

    const columns = [
      { header: t('table.no'), key: 'no' },
      { header: t('table.username'), key: 'username' },
      { header: t('table.name'), key: 'name' },
      { header: t('table.address'), key: 'address' },
      { header: t('table.representative'), key: 'representative' },
      { header: t('table.phone'), key: 'phone' },
      { header: t('table.email'), key: 'email' },
      { header: t('table.totalDevices'), key: 'total_devices' },
      { header: t('table.totalUsers'), key: 'total_users' },
      { header: t('table.createdAt'), key: 'created_at' },
    ];

    const data = res.map((device, index) => ({
      ...device,
      no: index + 1,
    }));

    exportExcel(columns, data, t('table.fileName'));
  };

  return (
    <Box className={cx('w-full', 'technician-screen')}>
      <Box className={cx('box-container')}>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='rating_status'>{t('rating')}</FormLabel>
            <BaseSelect
              options={ratingStatusOptions}
              value={form.values.rating_status}
              placeholder={t('select')}
              name='rating_status'
              isSearchEnable
              onChange={form.handleChange}
              displayInvalidValue
              isDisplayAllOption
            />
          </Box>
        </Box>
        <Box className='flex flex-row items-center gap-[20px]'>
          <FormLabel htmlFor='date_to'>{t('createdDate')}</FormLabel>
          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='date_from'>{t('start')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={form.values.date_from}
                dateFormat='YYYY-MM-DD'
                onChange={(date) => form.setFieldValue('date_from', dayjs(date).format('YYYY-MM-DD'))}
                placeholder={t('select')}
                calendarProps={form.values.date_to ? { maxDate: new Date(form.values.date_to) } : {}}
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='date_to'>{t('end')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={form.values.date_to}
                dateFormat='YYYY-MM-DD'
                onChange={(date) => form.setFieldValue('date_to', dayjs(date).format('YYYY-MM-DD'))}
                placeholder={t('select')}
                calendarProps={form.values.date_from ? { minDate: new Date(form.values.date_from) } : {}}
              />
            </Box>
          </Box>
        </Box>
        <Box className='text-center'>
          <Button
            className='bg-[#ED1002] rounded-[20px] text-white w-[130px] h-[35px] hover:bg-[#ED1002] hover:text-white hover:opacity-80'
            onClick={() => {
              clearInputFilter();
              form.resetForm();
            }}
          >
            {t('clearFilter')}
          </Button>
        </Box>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          mt: 3,
          mb: 2,
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-2 px-5 justify-between items-center'>
          <Typography className='text-[16px] font-bold text-[white]'>{t('title')}</Typography>
          <Box className='flex pr-10'>
            <Button
              startIcon={<AddNewIcon />}
              className='text-white'
              onClick={() => {
                technician.current = null;
                setIsOpenEditTechnicianDialog(true);
              }}
            >
              {t('addNew')}
            </Button>

            <Button startIcon={<ExportFillIcon />} className='text-white ' onClick={exportTechnicians}>
              {t('fileExport')}
            </Button>
          </Box>
        </Box>
        <TechnicianTable
          pageSize={form.values.page_size ?? 10}
          data={data}
          form={form}
          technicianIds={technicianIds}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
          onEdit={(row) => {
            technician.current = row;
            setIsOpenEditTechnicianDialog(true);
          }}
          onDetail={(row) => {
            technician.current = row;
            setIsOpenDetailTechnicianDialog(true);
          }}
          onReset={(row) => {
            technician.current = row;
            setOpenUpdatePasswordDialog(true);
          }}
          onDelete={(row) => {
            technician.current = row;
            setIsOpenDeleteTechnicianDialog(true);
          }}
          setRefreshData={setRefreshData}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('record')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={[
              { id: 10, name: '10' },
              { id: 20, name: '20' },
              { id: 50, name: '50' },
              { id: 100, name: '100' },
              { id: 250, name: '250' },
              { id: 500, name: '500' },
            ]}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>
      <DetailTechnicianDialog
        open={isOpenDetailTechnicianDialog}
        setOpen={setIsOpenDetailTechnicianDialog}
        data={technician.current as any}
      />
      <EditTechnicianDialog
        open={isOpenEditTechnicianDialog}
        setOpen={(open, refreshData) => {
          setIsOpenEditTechnicianDialog(open);
          refreshData && handleGetTechnicians();
        }}
        data={technician.current as any}
        mode={technician.current ? DIALOG_MODE_EDIT : DIALOG_MODE_CREATE}
      />
      <UpdatePasswordDialog
        open={openUpdatePasswordDialog}
        setOpen={setOpenUpdatePasswordDialog}
        data={technician.current as IAccount}
      />
      <ConfirmDialog
        open={isOpenDeleteTechnicianDialog}
        title={t('deleteTechnicianDialog.title')}
        description={t('deleteTechnicianDialog.description', {
          username: technician.current?.username,
        })}
        onConfirm={() => handleDeleteTechnician(true)}
        onCancel={() => setIsOpenDeleteTechnicianDialog(false)}
      />
    </Box>
  );
};

export default memo(Technician);
